/* Engravita v7 · "Meridian" from the GetLayers library (gradients/meridian.html), ported to a theme asset.
   One line of light across the frame with a falloff: the brand's laser line, drawn on a thin strip laid over a rule. Plain WebGL2, one triangle,
   the shader is the library's own (motion layer preserved, comments stripped); only CONFIG is tinted, to the
   Engravita night (--eg-shade) and the brand purple. Mounts on every [data-eg7-light] canvas and sizes to the canvas itself, renders only
   while on screen, follows the pointer inside the nearest [data-eg7-host], and draws ONE still frame under prefers-reduced-motion. */
(()=>{
const BASE={bgColor:'#120818',colorA:'#1E1027',colorB:'#500778',colorC:'#8E4BBF',colorD:'#F6EEFB',speed:.33,tilt:0,level:0,arc:0,centerX:0,centerY:0,sway:.02,core:.022,reach:.16,bright:1,skirt:.22,ambient:.04,warp:.05,scale:.4,drift:.07,roughness:.5,lacunarity:2,contrast:1.25,midpoint:.42,sink:.14,glow:.15,seam:.12,split:0,grain:.018,grainAnim:1,dither:1.5,vignette:.05,lift:.05,open:.15,pourRadius:.55,pour:.05,smear:.3,infuse:.04,cursor:1,parallax:.002,maxDpr:1.5};
const VERT=`#version 300 es
void main(){vec2 p=vec2((gl_VertexID<<1)&2,gl_VertexID&2);gl_Position=vec4(p*2.0-1.0,0.0,1.0);}`;
const FRAG=`#version 300 es
precision highp float;
out vec4 fragColor;
uniform vec2  iResolution;
uniform float iTime;
uniform vec2  iMouse;
uniform vec2  iLead;
uniform float uPourRadius, uPour, uSmear, uInfuse;
uniform vec3  uBg, uColorA, uColorB, uColorC, uColorD;
uniform float uSpeed, uTilt, uLevel, uSway, uCore, uReach;
uniform float uBright, uSkirt, uAmbient, uWarp, uScale, uDrift;
uniform float uRoughness, uLacunarity, uContrast, uMidpoint, uSink, uGlow, uSeam, uSplit;
uniform float uGrain, uDither, uVignette, uLift, uOpen, uParallax;
uniform float uArc, uCenterX, uCenterY;
#define OCTAVES 3
vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}
float snoise(vec2 p) {
  const float K1 = 0.366025404, K2 = 0.211324865;
  vec2 i = floor(p + (p.x + p.y) * K1);
  vec2 a = p - i + (i.x + i.y) * K2;
  float m = step(a.y, a.x);
  vec2 o = vec2(m, 1.0 - m);
  vec2 b = a - o + K2;
  vec2 c = a - 1.0 + 2.0 * K2;
  vec3 h = max(0.5 - vec3(dot(a, a), dot(b, b), dot(c, c)), 0.0);
  vec3 n = h * h * h * h * vec3(dot(a, hash2(i)), dot(b, hash2(i + o)), dot(c, hash2(i + 1.0)));
  return dot(n, vec3(70.0));
}
float fbm(vec2 p) {
  float v = 0.0, amp = 0.5;
  for (int i = 0; i < OCTAVES; i++) {
    v += amp * snoise(p);
    p *= uLacunarity;
    amp *= uRoughness;
  }
  return v;
}
vec3 ramp4(float t, float side) {
  vec3 acc = mix(uColorB, uColorC, side);
  vec3 lo  = mix(uColorB, acc, uSplit);
  vec3 hi  = mix(uColorC, acc, uSplit);
  vec3 c = mix(uColorA, lo, smoothstep(0.00, 0.36, t));
  float k = smoothstep(0.32, 0.70, t);
  c = mix(c, hi, k);
  c = mix(c, uColorD, uSeam * 4.0 * k * (1.0 - k));
  c = mix(c, uColorD, smoothstep(0.66, 1.00, t));
  return c;
}
float triDither(vec2 fc) {
  float a = fract(sin(dot(fc, vec2(12.9898, 78.233))) * 43758.5453);
  float b = fract(sin(dot(fc + 17.0, vec2(12.9898, 78.233))) * 43758.5453);
  return (a + b - 1.0) / 255.0;
}
vec2 rot(vec2 p, float a) { float s = sin(a), c = cos(a); return mat2(c, -s, s, c) * p; }
uniform float uGrainAnim;
float houseGrain(vec2 fc) {
  uvec2 q = uvec2(fc) * uvec2(1597334677u, 3812015801u)
          + uint(floor(iTime * 24.0 * uGrainAnim)) * 2654435769u;
  uint n = q.x ^ q.y; n = n * 1664525u + 1013904223u; n ^= n >> 16u; n *= 2246822519u; n ^= n >> 13u;
  float a = float(n & 0xffffu) / 65535.0;
  n *= 3266489917u; n ^= n >> 16u;
  float b = float(n & 0xffffu) / 65535.0;
  return a + b - 1.0;
}
void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * iResolution) / iResolution.y;
  float t = iTime * uSpeed;
  vec2 tfD = uv - iMouse;
  vec2 tfStroke = iLead - iMouse;
  float tfR2 = max(1e-4, uPourRadius * uPourRadius);
  float tfWell = exp(-dot(tfD, tfD) / tfR2);
  float tfWide = exp(-dot(tfD, tfD) / (tfR2 * 3.0));
  vec2 tfPour = -tfD * tfWell * uPour + tfStroke * tfWide * uSmear;
  float tfInfuse = tfWide * uInfuse * (0.55 + 0.45 * smoothstep(0.0, 0.12, length(tfStroke)));
  vec2 p = uv + tfPour - iMouse * uParallax;
  vec2 n = vec2(-sin(uTilt), cos(uTilt));
  vec2 q = p - vec2(uCenterX, uCenterY);
  float air = fbm(p * uScale + vec2(-t * uDrift, t * uDrift * 0.5));
  float lvl = uLevel + sin(t * 0.13) * uSway + iMouse.y * uLift;
  float d = mix(dot(q, n) - lvl, length(q) - max(0.0, lvl), uArc) + air * uWarp;
  float w = max(0.004, uCore * (1.0 + iMouse.x * uOpen));
  float band = exp(-(d * d) / (w * w));
  float wide = exp(-abs(d) / max(0.02, uReach));
  float f = uAmbient + uBright * band + uSkirt * wide + tfInfuse;
  f = clamp((f - uMidpoint) * uContrast + 0.5, 0.0, 1.0);
  float side = smoothstep(-1.0, 1.0, d / max(0.03, uCore + uReach * 0.35));
  vec3 col = ramp4(f, side);
  col += uColorD * uGlow * pow(f, 4.0);
  col = mix(uBg, col, smoothstep(0.0, max(0.01, uSink), f) * 0.90 + 0.10);
  col *= 1.0 - uVignette * dot(uv, uv);
  { float hgL = clamp(dot(col, vec3(0.299, 0.587, 0.114)), 0.0, 1.0);
    col += houseGrain(gl_FragCoord.xy) * uGrain * mix(1.0, 4.0 * hgL * (1.0 - hgL), 0.6); }
  col += triDither(gl_FragCoord.xy) * uDither;
  fragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;
const reduce=matchMedia('(prefers-reduced-motion: reduce)').matches;
const hex=h=>{const n=parseInt(h.slice(1),16);return[(n>>16&255)/255,(n>>8&255)/255,(n&255)/255]};
const U={bgColor:'uBg',colorA:'uColorA',colorB:'uColorB',colorC:'uColorC',colorD:'uColorD'};
function mount(canvas){
  if(canvas.__eg7)return;canvas.__eg7=1;
  const C=Object.assign({},BASE);
  try{Object.assign(C,JSON.parse(canvas.dataset.eg7Light||'{}'))}catch(e){}
  const gl=canvas.getContext('webgl2',{alpha:false,antialias:false,depth:false,stencil:false,powerPreference:'low-power'});
  if(!gl){canvas.remove();return}
  const sh=(t,src)=>{const s=gl.createShader(t);gl.shaderSource(s,src);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(s));return s};
  const pr=gl.createProgram();
  try{gl.attachShader(pr,sh(gl.VERTEX_SHADER,VERT));gl.attachShader(pr,sh(gl.FRAGMENT_SHADER,FRAG));gl.linkProgram(pr)}catch(e){canvas.remove();return}
  gl.useProgram(pr);gl.bindVertexArray(gl.createVertexArray());
  const L={},loc=n=>n in L?L[n]:(L[n]=gl.getUniformLocation(pr,n));
  for(const k in C){if(k==='cursor'||k==='maxDpr')continue;const u=U[k]||('u'+k[0].toUpperCase()+k.slice(1));if(typeof C[k]==='string'){const c=hex(C[k]);gl.uniform3f(loc(u),c[0],c[1],c[2])}else gl.uniform1f(loc(u),C[k])}
  const host=canvas.closest('[data-eg7-host]')||canvas.parentElement;
  const size=()=>{const r=canvas.getBoundingClientRect(),d=Math.min(devicePixelRatio||1,C.maxDpr),w=Math.max(1,Math.round(r.width*d)),h=Math.max(1,Math.round(r.height*d));if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h}gl.viewport(0,0,w,h);gl.uniform2f(loc('iResolution'),w,h)};
  size();new ResizeObserver(()=>{size();if(reduce)draw()}).observe(canvas);
  const m={x:0,y:0,ax:0,ay:0,tx:0,ty:0};
  if(C.cursor&&!reduce)host.addEventListener('pointermove',e=>{const r=canvas.getBoundingClientRect(),a=r.width/r.height;m.tx=Math.max(-a/2,Math.min(a/2,((e.clientX-r.left)/r.width-.5)*a));m.ty=Math.max(-.35,Math.min(.35,.5-(e.clientY-r.top)/r.height))},{passive:true});
  let clock=4.2,prev=performance.now(),vis=false;
  const draw=()=>{gl.uniform1f(loc('iTime'),clock);gl.uniform2f(loc('iMouse'),m.x,m.y);gl.uniform2f(loc('iLead'),m.ax,m.ay);gl.drawArrays(gl.TRIANGLES,0,3)};
  draw();canvas.classList.add('is-on');
  if(reduce)return;
  new IntersectionObserver(es=>{vis=es[0].isIntersecting;if(vis){prev=performance.now();requestAnimationFrame(f)}},{threshold:0}).observe(canvas);
  function f(now){if(!vis||document.hidden)return;requestAnimationFrame(f);const raw=now-prev;prev=now;const ms=raw>50?50:raw<4.167?4.167:raw,s=ms>36.7?2.2:ms*.06;clock+=ms*.001;const kl=.105*s,kb=.043*s;m.ax+=(m.tx-m.ax)*kl;m.ay+=(m.ty-m.ay)*kl;m.x+=(m.ax-m.x)*kb;m.y+=(m.ay-m.y)*kb;draw()}
  document.addEventListener('visibilitychange',()=>{if(!document.hidden&&vis){prev=performance.now();requestAnimationFrame(f)}});
}
const init=()=>document.querySelectorAll('canvas[data-eg7-light]').forEach(c=>{try{mount(c)}catch(e){}});
document.readyState==='loading'?document.addEventListener('DOMContentLoaded',init):init();
document.addEventListener('shopify:section:load',init);
})();
